import { type ReactElement } from 'react';
import {
  Outlet,
  createFileRoute,
  Link,
  redirect,
} from '@tanstack/react-router';

import { BillingBanner } from '@/components/billing/billing-banner';
import { CreditBalanceChip } from '@/components/billing/credit-balance-chip';
import { WizardCopilotPanel } from '@/components/wizard/copilot/wizard-copilot-panel';
import { WizardCancelButton } from '@/components/wizard/shared/wizard-cancel-button';
import { WizardProvider } from '@/components/wizard/shared/wizard-context';
import { WizardStepper } from '@/components/wizard/shared/wizard-stepper';
import { BRAND_NAME } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { canCreateStrategy } from '@/lib/roles';

export const Route = createFileRoute('/_authenticated/wizard')({
  staticData: { hideAppShell: true, title: 'route.wizard.title' },
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient
      .fetchQuery(meQueryOptions)
      .catch(() => null);
    if (user && !canCreateStrategy(user)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: ROUTES.DASHBOARD, replace: true });
    }
  },
  component: WizardLayout,
});

function WizardLayout(): ReactElement {
  return (
    <WizardProvider>
      <div data-fullbleed-layout className="bg-bg-page flex min-h-svh">
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="border-border-1 bg-bg-surface sticky top-0 z-20 flex items-center gap-3 border-b px-4 py-3 sm:gap-4 sm:px-6 sm:py-4">
            <Link
              to="/dashboard"
              className="text-fs-14 text-fg-1 inline-flex shrink-0 items-center gap-2 font-semibold"
            >
              <img src="/images/logo.svg" alt={BRAND_NAME} className="h-6.5" />
            </Link>
            <div className="flex min-w-0 flex-1">
              <WizardStepper />
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <CreditBalanceChip />
              <WizardCancelButton />
            </div>
          </header>
          <BillingBanner />
          <main className="mx-auto w-full max-w-285 px-4 py-6 sm:px-6 sm:py-8">
            <Outlet />
          </main>
        </div>
        <WizardCopilotPanel />
      </div>
    </WizardProvider>
  );
}
